/**
 * Vector Pixelate tab: import, layer selection with per-layer cut params,
 * pixel/edge/photo-fill settings, safety limits, and the generate/export
 * actions. Live preview recomputes on any geometry-affecting change; exports
 * stay gated until a full, non-truncated generation matches current settings.
 */
import type {
  CutParams,
  DotShape,
  EdgeMode,
  GridOrigin,
  LayerSortMode,
  PaletteMode,
  PhotoFillSettings,
  PixelMode,
  PixelSettings,
  VectorDocument,
} from "../../core/types";
import { LIGHTBURN_PALETTE } from "../../core/types";
import {
  button,
  checkboxField,
  clearNode,
  cutParamsEditor,
  dimField,
  dimSlider,
  el,
  numberField,
  radioField,
  section,
  selectField,
  sliderField,
  swatch,
  vectorExportReady,
  type PanelContext,
  type PanelHandle,
} from "../app";
import {
  DEFAULT_CUT_PARAMS,
  MATERIAL_PRESETS,
  PRESET_DISCLAIMER,
  nearestPresetParams,
} from "../presets";

const SHAPE_OPTS: { value: DotShape; label: string }[] = [
  { value: "circle", label: "Circle" },
  { value: "square", label: "Square" },
  { value: "diamond", label: "Diamond" },
  { value: "hexagon", label: "Hexagon" },
  { value: "octagon", label: "Octagon" },
  { value: "triangle", label: "Triangle" },
  { value: "star", label: "Star" },
  { value: "heart", label: "Heart" },
  { value: "cross", label: "Cross" },
];
const PALETTE_OPTS: { value: PaletteMode; label: string }[] = [
  { value: "adaptive", label: "Adaptive (image colors)" },
  { value: "lightburn", label: "LightBurn palette (exact)" },
];
const SORT_OPTS: { value: LayerSortMode; label: string }[] = [
  { value: "dark-to-light", label: "Dark → light" },
  { value: "light-to-dark", label: "Light → dark" },
  { value: "most-used", label: "Most used" },
  { value: "hue", label: "Hue" },
];

export function createVectorPanel(ctx: PanelContext): PanelHandle {
  const { store, actions } = ctx;
  const root = el("div");

  const invalidateAndPreview = (): void => {
    store.set({ vectorFullSignature: null });
    actions.scheduleVectorPreview();
  };
  const updatePixel = (patch: Partial<PixelSettings>): void => {
    store.set((s) => ({ pixel: { ...s.pixel, ...patch } }));
    actions.persist();
    invalidateAndPreview();
  };
  const updateFill = (patch: Partial<PhotoFillSettings>): void => {
    store.set((s) => ({ photoFill: { ...s.photoFill, ...patch } }));
    actions.persist();
    invalidateAndPreview();
  };

  const s0 = store.get();

  // ---- import -------------------------------------------------------------
  const importSection = section("Source file", { open: true });
  const importBtn = button("Import .lbrn / .lbrn2 / .svg", { variant: "primary full", onClick: () => actions.browseVector() });
  importSection.content.append(
    importBtn,
    el("p", { class: "section-hint", text: "Or drag a file onto the window, or paste with Ctrl+V." }),
  );

  // ---- layers -------------------------------------------------------------
  const layersSection = section("Layers", { open: true });
  const layerList = el("div", { class: "layer-list" });
  // LightBurn-style single parameter table that edits whichever layer is active.
  const layerParamPanel = el("div", { class: "layer-param-panel" });
  let activeParamLayer: number | null = null;
  const summarySpans = new Map<number, HTMLElement>();
  const presetSelect = selectField({
    label: "Material preset",
    title: "Seeds starting cut parameters for a machine + material. These are starting points — calibrate before relying on them.",
    value: "0",
    options: MATERIAL_PRESETS.map((p, i) => ({ value: String(i), label: p.name })),
    onChange: () => {},
  });
  const applyBtn = button("Apply preset to selected", {
    variant: "sm",
    title: "Fill each selected layer's cut parameters from the nearest preset color.",
    onClick: () => applyPreset(),
  });
  const selectAllBtn = button("Select all", { variant: "sm ghost", onClick: () => setAllSelected(true) });
  const clearBtn = button("Clear", { variant: "sm ghost", onClick: () => setAllSelected(false) });
  layersSection.content.append(
    layerList,
    layerParamPanel,
    el("div", { class: "btn-row" }, [selectAllBtn, clearBtn]),
    presetSelect.root,
    el("div", { class: "btn-row" }, [applyBtn]),
    el("p", { class: "disclaimer", text: PRESET_DISCLAIMER }),
  );

  function layerColor(doc: VectorDocument, index: number): string {
    const li = doc.layers.find((l) => l.index === index);
    return li ? li.color : LIGHTBURN_PALETTE[((index % 30) + 30) % 30];
  }

  function applyPreset(): void {
    const s = store.get();
    if (!s.doc) return;
    const preset = MATERIAL_PRESETS[Number(presetSelect.get())] ?? MATERIAL_PRESETS[0];
    const cp: Record<number, CutParams> = { ...s.cutParams };
    for (const layer of s.selectedLayers) {
      const p = nearestPresetParams(preset, layerColor(s.doc, layer));
      if (p) cp[layer] = p;
    }
    store.set({ cutParams: cp });
    actions.persist();
    renderLayers();
    actions.toast("success", "Applied " + preset.name + " to selected layers.");
  }

  function setAllSelected(on: boolean): void {
    const s = store.get();
    if (!s.doc) return;
    const sel = new Set<number>();
    if (on) for (const l of s.doc.layers) sel.add(l.index);
    store.set({ selectedLayers: sel, vectorFullSignature: null });
    renderLayers();
    actions.scheduleVectorPreview();
  }

  function toggleLayer(index: number, on: boolean, row: HTMLElement): void {
    store.set((s) => {
      const sel = new Set(s.selectedLayers);
      if (on) sel.add(index);
      else sel.delete(index);
      return { selectedLayers: sel, vectorFullSignature: null };
    });
    row.classList.toggle("selected", on);
    actions.scheduleVectorPreview();
  }

  /** Params for a layer: stored value, else nearest-preset seed, else default. */
  function paramsForLayer(index: number, color: string): CutParams {
    const stored = store.get().cutParams[index];
    if (stored) return stored;
    const preset = MATERIAL_PRESETS[Number(presetSelect.get())] ?? MATERIAL_PRESETS[0];
    return nearestPresetParams(preset, color) ?? DEFAULT_CUT_PARAMS;
  }

  function summaryText(p: CutParams): string {
    const n = (v: number): string => (Number.isInteger(v) ? String(v) : String(Number(v.toFixed(2))));
    return `${n(p.speed)} mm/s · ${n(p.power)}%`;
  }

  /** Rebuild the single parameter table for the active layer (LightBurn-style). */
  function renderParamPanel(): void {
    clearNode(layerParamPanel);
    const s = store.get();
    if (!s.doc || !s.doc.layers.length || activeParamLayer === null) {
      layerParamPanel.append(
        el("p", { class: "section-hint", text: "Select a layer above to edit its cut parameters." }),
      );
      return;
    }
    const li = s.doc.layers.find((l) => l.index === activeParamLayer);
    if (!li) {
      layerParamPanel.append(
        el("p", { class: "section-hint", text: "Select a layer above to edit its cut parameters." }),
      );
      return;
    }
    const initial = paramsForLayer(li.index, li.color);
    // Seed the store so what the editor shows is exactly what exports embed.
    if (!store.get().cutParams[li.index]) {
      store.set((st) => ({ cutParams: { ...st.cutParams, [li.index]: initial } }));
      actions.persist();
    }
    const header = el("div", { class: "layer-param-head" }, [
      swatch(li.color),
      el("span", { class: "layer-param-title", text: `Parameters — ${li.name}` }),
    ]);
    layerParamPanel.append(
      header,
      cutParamsEditor(initial, (p) => {
        store.set((st) => ({ cutParams: { ...st.cutParams, [li.index]: p } }));
        actions.persist();
        const span = summarySpans.get(li.index);
        if (span) span.textContent = summaryText(p);
      }),
    );
  }

  function setActiveParamLayer(index: number): void {
    activeParamLayer = index;
    for (const r of layerList.querySelectorAll<HTMLElement>(".layer-row")) {
      r.classList.toggle("active", Number(r.dataset.layer) === index);
    }
    renderParamPanel();
  }

  function renderLayers(): void {
    clearNode(layerList);
    summarySpans.clear();
    const s = store.get();
    if (!s.doc) {
      layerList.append(el("p", { class: "section-hint", text: "No file loaded yet." }));
      layersSection.setBadge(null);
      activeParamLayer = null;
      renderParamPanel();
      return;
    }
    if (!s.doc.layers.length) {
      layerList.append(el("p", { class: "notice warn", text: "No layers with importable shapes were found." }));
      layersSection.setBadge(null);
      activeParamLayer = null;
      renderParamPanel();
      return;
    }
    layersSection.setBadge(`${s.selectedLayers.size}/${s.doc.layers.length}`);
    // Keep the active layer valid; default to the first layer.
    if (activeParamLayer === null || !s.doc.layers.some((l) => l.index === activeParamLayer)) {
      activeParamLayer = s.doc.layers[0].index;
    }
    for (const li of s.doc.layers) {
      const selected = s.selectedLayers.has(li.index);
      const isActive = li.index === activeParamLayer;
      const row = el("div", { class: "layer-row" + (selected ? " selected" : "") + (isActive ? " active" : "") });
      row.dataset.layer = String(li.index);
      const check = el("input", { attrs: { type: "checkbox" } }) as HTMLInputElement;
      check.checked = selected;
      check.title = "Include this layer in the output";
      check.addEventListener("change", () => {
        toggleLayer(li.index, check.checked, row);
        setActiveParamLayer(li.index);
      });
      const meta = el("span", { class: "layer-meta", text: `${li.shapeCount} shape${li.shapeCount === 1 ? "" : "s"}` });
      const summary = el("span", { class: "layer-summary", text: summaryText(paramsForLayer(li.index, li.color)) });
      summarySpans.set(li.index, summary);
      const nameEl = el("span", { class: "layer-name", text: li.name, title: li.name });
      const head = el("div", { class: "layer-head" }, [check, swatch(li.color), nameEl, meta, summary]);
      // Clicking the row body selects it as the active layer for the parameter
      // table below (LightBurn behaviour); the checkbox handles output on/off.
      head.setAttribute("role", "button");
      head.setAttribute("tabindex", "0");
      head.setAttribute("aria-label", `Edit parameters for ${li.name}`);
      const activate = (e: Event): void => {
        if (e.target === check) return;
        setActiveParamLayer(li.index);
      };
      head.addEventListener("click", activate);
      head.addEventListener("keydown", (e) => {
        if (e.target === check) return;
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          setActiveParamLayer(li.index);
        }
      });
      row.append(head);
      layerList.append(row);
    }
    renderParamPanel();
  }

  // ---- dots ---------------------------------------------------------------
  const pixSection = section("Dots", { open: true });
  pixSection.content.append(
    dimSlider(ctx, { label: "Dot size", valueMm: s0.pixel.pixelSize, minMm: 0.01, maxMm: 2, stepMm: 0.01, title: "Dot diameter / width. Below ~0.05 mm oxide colors need a filled area to read.", onChangeMm: (v) => updatePixel({ pixelSize: v }) }),
    dimSlider(ctx, { label: "Detail (dot pitch) — across", valueMm: s0.pixel.spacingX, minMm: 0.01, maxMm: 3, stepMm: 0.01, title: "Horizontal spacing between dot centers. Smaller = finer detail, more dots.", onChangeMm: (v) => updatePixel({ spacingX: v }) }),
    dimSlider(ctx, { label: "Detail (dot pitch) — down", valueMm: s0.pixel.spacingY, minMm: 0.01, maxMm: 3, stepMm: 0.01, title: "Vertical spacing between dot centers. Smaller = finer detail, more dots.", onChangeMm: (v) => updatePixel({ spacingY: v }) }),
    selectField<DotShape>({ label: "Dot shape", value: s0.pixel.shape, options: SHAPE_OPTS, onChange: (v) => updatePixel({ shape: v }) }).root,
    sliderField({ label: "Dot stretch (height)", value: s0.pixel.aspect, min: 0.25, max: 4, step: 0.05, title: "Stretch each dot vertically. 1.00 = as drawn; above 1 makes a diamond a tall rhombus, a circle an oval, etc.", format: (v) => v.toFixed(2) + "×", onChange: (v) => updatePixel({ aspect: v }) }).root,
    checkboxField({ label: "Flip shape on offset rows", title: "Mirror the dot vertically on every other row — hearts alternate point-down / point-up so they nest.", checked: s0.pixel.flipOffset, onChange: (v) => updatePixel({ flipOffset: v }) }).root,
    radioField<PixelMode>({ label: "Fill mode", value: s0.pixel.mode, title: "Auto picks fill or trace per shape; Fill = solid dot grid; Trace = follow the outline.", options: [{ value: "auto", label: "Auto" }, { value: "fill", label: "Fill" }, { value: "trace", label: "Trace" }], onChange: (v) => updatePixel({ mode: v }) }).root,
    selectField<GridOrigin>({ label: "Grid origin", title: "Where the dot grid is anchored: to the shape bounds, or to the project's (0,0).", value: s0.pixel.origin, options: [{ value: "bbox", label: "Shape bounds" }, { value: "project", label: "Project grid (0,0)" }], onChange: (v) => updatePixel({ origin: v }) }).root,
    el("div", { class: "field-row" }, [
      dimField(ctx, { label: "Nudge across", valueMm: s0.pixel.offsetX, stepMm: 0.01, onChangeMm: (v) => updatePixel({ offsetX: v }) }),
      dimField(ctx, { label: "Nudge down", valueMm: s0.pixel.offsetY, stepMm: 0.01, onChangeMm: (v) => updatePixel({ offsetY: v }) }),
    ]),
    checkboxField({ label: "Offset every other row", title: "Stagger alternate rows by half a step for a honeycomb-like pack.", checked: s0.pixel.stagger, onChange: (v) => updatePixel({ stagger: v }) }).root,
    checkboxField({ label: "Hex packing (equal spacing)", title: "Lock the down-pitch to across × 0.866 and stagger rows, so every dot is the same distance from its six neighbours — a true honeycomb.", checked: s0.pixel.hexPacking, onChange: (v) => updatePixel({ hexPacking: v }) }).root,
    sliderField({ label: "Curve smoothness", value: s0.pixel.curveSteps, min: 3, max: 96, step: 1, title: "Bezier flattening steps for curved outlines. Higher = smoother curves, slower.", onChange: (v) => updatePixel({ curveSteps: Math.round(v) }) }).root,
  );

  // ---- edge dots ----------------------------------------------------------
  const edgeSection = section("Edge dots", { open: true, hint: "How dots along the outline of a shape are handled." });
  edgeSection.content.append(
    radioField<EdgeMode>({
      label: "Edge dots",
      value: s0.pixel.edgeMode,
      stack: true,
      options: [
        { value: "clip", label: "Trace edges (crisp, follows the outline)" },
        { value: "none", label: "Off (plain grid only)" },
      ],
      onChange: (v) => updatePixel({ edgeMode: v }),
    }).root,
  );

  // ---- photo fill ---------------------------------------------------------
  const fillSection = section("Photo fill", { open: false, hint: "Colorize the dots inside your vector from an image." });
  const fillControls = el("div", { class: "field" });
  const fillImageBtn = button("Choose image…", { variant: "sm", onClick: () => actions.browsePhotoFillImage() });
  const fillImageName = el("p", { class: "section-hint", text: s0.photoFillImage ? s0.photoFillImage.name : "No image chosen." });
  fillControls.append(
    fillImageBtn,
    fillImageName,
    selectField({ label: "Fit", value: s0.photoFill.fit, options: [{ value: "cover", label: "Cover" }, { value: "contain", label: "Contain" }, { value: "stretch", label: "Stretch" }], onChange: (v) => updateFill({ fit: v }) }).root,
    sliderField({ label: "Max layers", value: s0.photoFill.maxLayers, min: 1, max: 30, step: 1, onChange: (v) => updateFill({ maxLayers: Math.round(v) }) }).root,
    selectField<PaletteMode>({ label: "Palette mode", value: s0.photoFill.paletteMode, options: PALETTE_OPTS, onChange: (v) => updateFill({ paletteMode: v }) }).root,
    selectField<LayerSortMode>({ label: "Layer order", value: s0.photoFill.sortMode, options: SORT_OPTS, onChange: (v) => updateFill({ sortMode: v }) }).root,
    numberField({ label: "First layer number", value: s0.photoFill.startLayer, min: 0, max: 29, step: 1, title: "First LightBurn cut-layer index to use (C00–C29).", onChange: (v) => updateFill({ startLayer: Math.round(v) }) }).root,
    checkboxField({ label: "Dither", checked: s0.photoFill.dither, onChange: (v) => updateFill({ dither: v }) }).root,
  );
  const fillEnable = checkboxField({
    label: "Enable photo fill",
    checked: s0.photoFill.enabled,
    onChange: (v) => {
      updateFill({ enabled: v });
      fillControls.style.display = v ? "" : "none";
      updateGating();
    },
  });
  fillControls.style.display = s0.photoFill.enabled ? "" : "none";
  fillSection.content.append(fillEnable.root, fillControls);

  // ---- safety -------------------------------------------------------------
  const safetySection = section("Safety", { open: false });
  safetySection.content.append(
    numberField({ label: "Max pixels", value: s0.pixel.maxPixels, min: 1, step: 10000, title: "Hard cap for the full generation — exports are blocked above it.", onChange: (v) => updatePixel({ maxPixels: Math.round(v) }) }).root,
    numberField({ label: "Live preview cap", unit: "dots", value: s0.livePreviewCap, min: 100, step: 5000, title: "Soft cap for the live preview only.", onChange: (v) => { store.set({ livePreviewCap: Math.max(100, Math.round(v)) }); actions.persist(); actions.scheduleVectorPreview(); } }).root,
  );

  // ---- actions ------------------------------------------------------------
  const actionsSection = section("Generate & export", { open: true });
  const genBtn = button("Generate full set", { variant: "primary full", onClick: () => actions.generateVector() });
  const gateNotice = el("div", { class: "notice", text: "" });
  const exportSvgBtn = button("Export SVG", { onClick: () => actions.exportVectorSvg() });
  const exportLbrnBtn = button("Export LightBurn copy", { onClick: () => actions.exportVectorLbrn() });
  const replaceChk = checkboxField({
    label: "Replace original vectors in the copy",
    sub: "Off keeps the artwork and adds dots on the same layers.",
    checked: s0.replaceOriginal,
    onChange: (v) => { store.set({ replaceOriginal: v }); actions.persist(); },
  });
  const csvBtn = button("Palette CSV", { onClick: () => actions.exportVectorPaletteCsv() });
  const reportBtn = button("Printable report", { onClick: () => actions.exportVectorReport() });
  const photoExportRow = el("div", { class: "btn-row" }, [csvBtn, reportBtn]);
  actionsSection.content.append(
    genBtn,
    gateNotice,
    el("div", { class: "btn-row" }, [exportSvgBtn, exportLbrnBtn]),
    replaceChk.root,
    photoExportRow,
  );

  // ---- reactive gating ----------------------------------------------------
  function gateReason(): string {
    const s = store.get();
    if (!s.doc) return "Open a file first.";
    if (s.selectedLayers.size === 0) return "Select at least one layer.";
    if (!s.vectorResult) return "Generate the full set to enable exports.";
    if (s.vectorTruncated) return "Result is truncated — raise the max-pixels cap, then generate.";
    if (!vectorExportReady(s)) return "Settings changed — generate the full set again.";
    return "";
  }

  function updateGating(): void {
    const s = store.get();
    const ready = vectorExportReady(s);
    const reason = ready ? "" : gateReason();
    genBtn.disabled = s.busy;
    genBtn.textContent = s.busy ? "Generating…" : "Generate full set";
    exportSvgBtn.disabled = !ready;
    exportLbrnBtn.disabled = !ready;
    exportSvgBtn.title = reason;
    exportLbrnBtn.title = reason;
    const fillReady = ready && s.photoFill.enabled && !!s.vectorFillResult;
    csvBtn.disabled = !fillReady;
    reportBtn.disabled = !fillReady;
    photoExportRow.style.display = s.photoFill.enabled ? "" : "none";
    if (!s.doc) {
      gateNotice.className = "notice";
      gateNotice.textContent = "Import a file to begin.";
    } else if (ready) {
      gateNotice.className = "notice accent";
      gateNotice.textContent = "Full set ready — exports enabled.";
    } else {
      gateNotice.className = "notice warn";
      gateNotice.textContent = reason;
    }
    fillImageName.textContent = s.photoFillImage ? s.photoFillImage.name : "No image chosen.";
  }

  root.append(
    importSection.root,
    layersSection.root,
    pixSection.root,
    edgeSection.root,
    fillSection.root,
    safetySection.root,
    actionsSection.root,
  );

  let lastDoc: VectorDocument | null = s0.doc;
  renderLayers();
  updateGating();
  store.subscribe(() => {
    const s = store.get();
    if (s.doc !== lastDoc) {
      lastDoc = s.doc;
      renderLayers();
    } else {
      layersSection.setBadge(s.doc ? `${s.selectedLayers.size}/${s.doc.layers.length}` : null);
    }
    updateGating();
  });

  return { element: root };
}
